import type { LoginHostSelection } from './types';

// Matches a Salesforce My Domain host. The subdomain is one or more
// dot-separated labels, because Salesforce inserts an infrastructure label for
// non-production orgs:
//   production        acme.my.salesforce.com
//   sandbox           acme--dev.sandbox.my.salesforce.com
//   developer/scratch orgfarm-93def4e4a6-dev-ed.develop.my.salesforce.com
// A single-label pattern rejects every sandbox and Developer Edition org — i.e.
// exactly the orgs an admin evaluates the integration with.
//
// Each label is `[a-z0-9]([a-z0-9-]*[a-z0-9])?`: it must start AND end with an
// alphanumeric. A trailing hyphen (`acme-`) is an invalid DNS label just like a
// leading one, and would otherwise pass through to OAuth as a malformed authority.
//
// One optional trailing slash is allowed: an admin who pastes a My Domain origin
// straight from the browser bar (`https://acme.my.salesforce.com/`) should not be
// rejected. It is stripped during normalization below so the value handed to OAuth
// matches the backend's expectation regardless of whether the slash was pasted.
const MY_DOMAIN_HOST_PATTERN =
  /^(https:\/\/)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.my\.salesforce\.com\/?$/i;

export function isValidMyDomainHost(input: string): boolean {
  return MY_DOMAIN_HOST_PATTERN.test(input.trim());
}

export function toLoginHost(
  selection: LoginHostSelection,
  customInput: string
): string | null {
  if (selection === 'production') {
    return 'production';
  }
  if (selection === 'sandbox') {
    return 'sandbox';
  }
  if (!isValidMyDomainHost(customInput)) {
    return null;
  }
  const domain = customInput
    .trim()
    .replace(/^https:\/\//i, '')
    .replace(/\/+$/, '')
    .toLowerCase();
  return `https://${domain}`;
}
